import React, { useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Row,
  Col,
  message,
  Divider,
  Space,
  Checkbox
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  LoginOutlined,
  SafetyOutlined
} from "@ant-design/icons";
import axios from "axios";
import Cookies from "js-cookie";
import "./signin.css";
import logo from "../../assets/logo.png";

const { Title, Text } = Typography;

function Signin() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const response = await axios.post("/auth/login", {
        email: values.email,
        password: values.password,
      });

      if (response.data.status === "SUCCESS") {
        message.success('Anmeldung erfolgreich!');
        const token = response.data.data.token;
        // Set cookie with appropriate expiration
        const expires = rememberMe ? 30 : 7; // 30 days if remember me, 7 days otherwise
        Cookies.set("jwtToken", token, { expires });
        // Also set in localStorage for API services
        localStorage.setItem("authToken", token);
        // Redirect to admin panel
        window.location.href = "/admin";
      } else {
        message.error(response.data.message || 'Anmeldung fehlgeschlagen');
      }
    } catch (error) {
      if (error.response) {
        message.error(error.response.data.message || 'Anmeldung fehlgeschlagen');
      } else {
        message.error('Netzwerkfehler. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="login-overlay"></div>
      </div>
      
      <div className="login-content">
        <Row justify="center" align="middle" style={{ minHeight: '100vh' }}>
          <Col xs={24} sm={20} md={16} lg={12} xl={8}>
            <Card 
              className="login-card"
              bordered={false}
              style={{
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                backdropFilter: 'blur(10px)',
                background: 'rgba(255, 255, 255, 0.95)'
              }}
            >
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <img
                    src={logo}
                    alt="Praxis am Ring Logo"
                    style={{
                      height: '80px',
                      width: 'auto',
                      marginBottom: '16px',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                    }}
                  />
                </div>
                <Title level={2} style={{ margin: 0, color: '#41b06e' }}>
                  Praxis am Ring
                </Title>
                <Text type="secondary" style={{ fontSize: '16px' }}>
                  Verwaltung der Arztpraxis
                </Text>
              </div>

              <Divider style={{ margin: '24px 0' }} />

              {/* Login Form */}
              <Form
                form={form}
                layout="vertical"
                onFinish={handleLogin}
                size="large"
                requiredMark={false}
              >
                <Form.Item
                  name="email"
                  label="E-Mail-Adresse"
                  rules={[
                    { required: true, message: 'Bitte geben Sie Ihre E-Mail-Adresse ein' },
                    { type: 'email', message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein' }
                  ]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder="E-Mail-Adresse eingeben"
                    style={{ 
                      borderRadius: '8px',
                      height: '48px'
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label="Passwort"
                  rules={[
                    { required: true, message: 'Bitte geben Sie Ihr Passwort ein' },
                    { min: 6, message: 'Das Passwort muss mindestens 6 Zeichen lang sein' }
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder="Passwort eingeben"
                    iconRender={(visible) => 
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                    style={{ 
                      borderRadius: '8px',
                      height: '48px'
                    }}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: '24px' }}>
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Checkbox
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      >
                        Angemeldet bleiben
                      </Checkbox>
                    </Col>
                    <Col>
                      <Text 
                        type="link" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => message.info('Bitte wenden Sie sich an den Administrator, um das Passwort zurückzusetzen')}
                      >
                        Passwort vergessen?
                      </Text>
                    </Col>
                  </Row>
                </Form.Item>

                <Form.Item style={{ marginBottom: '16px' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<LoginOutlined />}
                    style={{
                      width: '100%',
                      height: '48px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '500'
                    }}
                  >
                    {loading ? 'Anmeldung läuft...' : 'Anmelden'}
                  </Button>
                </Form.Item>

                <div style={{ textAlign: 'center' }}>
                  <Space>
                    <SafetyOutlined style={{ color: '#52c41a' }} />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Sicheres Login mit SSL-Verschlüsselung
                    </Text>
                  </Space>
                </div>
              </Form>

              {/* Footer */}
              <Divider style={{ margin: '32px 0 16px 0' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Verwaltungssystem für Arztpraxen
                </Text>
              </Divider>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default Signin;